import { IsoDate, IsoDateTime, IsoDateTimeTz, IsoDuration } from '../../../types/iso-date';
import { deconstructDate } from '../../helper/deconstruct-date.function';
import { constructDate } from '../../helper/construct-date.function';
import { addMillisecondsToDuration } from '../../helper/add-milliseconds-to-duration.function';
import { SECONDS_IN_DAY } from '../../helper/duration.constants';

/**
 * Add a number of days to an ISO date/date-time string while preserving its shape.
 *
 * Supported inputs:
 * - Date-only: YYYY-MM-DD -> returns YYYY-MM-DD
 * - Date-time (no tz): YYYY-MM-DDTHH:mm:ss(.fraction[1-6])? -> returns same shape
 * - Date-time with tz: YYYY-MM-DDTHH:mm:ss(.fraction[1-6])?(Z|±HH:MM) -> returns same shape, preserving the original tz literal
 *
 * Notes:
 * - The function only modifies the calendar date part; the time of day and fractional seconds are preserved verbatim.
 * - For timezone-bearing inputs, the original timezone suffix is preserved literally (no DST recalculation).
 * - `amount` must be a finite integer (can be negative).
 */
export function addDays<T extends IsoDate | IsoDateTime | IsoDateTimeTz | IsoDuration>(input: T, amount: number): T {
  if(!Number.isInteger(amount) || !isFinite(amount))
    throw new Error('addDays: amount must be a finite integer');
  if(amount === 0)
    return input;

  const deconstructed = deconstructDate(input);
  if(deconstructed._type !== 'date'
    && deconstructed._type !== 'dateTime'
    && deconstructed._type !== 'dateTimeTz'
    && deconstructed._type !== 'duration') {
    throw new Error('addDays: invalid ISO date/time/duration string');
  }
  if(deconstructed._type === 'duration') {
    const returnValue = addMillisecondsToDuration(deconstructed, amount * 1000 * SECONDS_IN_DAY);
    return constructDate(returnValue) as unknown as T;
  }
  const {year, month, day} = deconstructed;
  const dt = new Date(Date.UTC(year, month - 1, day));
  dt.setUTCDate(dt.getUTCDate() + amount);
  deconstructed.year = dt.getUTCFullYear();
  deconstructed.month = dt.getUTCMonth() + 1;
  deconstructed.day = dt.getUTCDate();
  return constructDate(deconstructed) as unknown as T;
}
